import { marketingColors as C, marketingFonts as F, visualTokens as V, homepageStyles, homepageTheme, marketingDarkStyles, withOpacity } from '../theme_landing_page_2';
import React, { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, ThemeProvider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead, { SITE_URL } from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import { QuoteIcon, InitialsAvatar } from '../marketing/components/icons';
import Eyebrow from '../marketing/components/Eyebrow';
import MillosHero from '../marketing/components/millos/MillosHero';
import MillosAgentsScroll from '../marketing/components/millos/MillosAgentsScroll';
import MillosOrbit from '../marketing/components/millos/MillosOrbit';
import MillosAvailabilityCta from '../marketing/components/millos/MillosAvailabilityCta';
import MillosProductPreview from '../marketing/components/millos/MillosProductPreview';
import StatsDotFlow from '../marketing/components/millos/StatsDotFlow';
import StepTimeline, { STEP_TIMELINE_CONFIG } from '../marketing/components/millos/StepTimeline';
import { getArticle, cover } from '../marketing/data';

const stats = [
  { v: '£30,000', l: 'Avoid penalties for compliance breaches', mobileLabel: 'Avoid compliance penalties' },
  { v: "2 years' rent", l: 'Avoid rent repayment for offences', mobileLabel: 'Avoid rent repayment' },
  { v: '4×', l: 'Provide quarterly updates within MTD', mobileLabel: 'MTD updates per year' },
  { v: '31 hrs', l: 'Saved time monthly on admin', mobileLabel: 'Admin saved monthly' },
];

const steps = [
  { n: '01', title: 'You connect once', bullets: ['Add your properties', 'Drop in your certificates', 'Link your bank read-only', 'Ten minutes, once.'] },
  { n: '02', title: 'AI reads it', bullets: ['Extracts every expiry', 'Matches rent to the right flat', 'Files each expense and certificate to the right property'] },
  { n: '03', title: 'AI agents do the work', bullets: ['Plans your certificate renewals', 'Coordinates inspection visits with engineers and tenants', 'Reconciles your property ledger'] },
  { n: '04', title: 'You stay compliant', bullets: ['Sends your figures to your accountant each quarter', 'Keeps an evidence pack ready per property'] },
];

const testimonials = [
  { name: 'James Whitfield', role: 'Portfolio landlord · 34 units', quote: "MillOS caught two Gas Safety certs about to lapse before I'd even noticed. It has genuinely made a missed deadline impossible across my portfolio." },
  { name: 'Priya Ramesh', role: 'Letting agent · Manchester', quote: 'The compliance score per property is the first thing my team checks every morning. Onboarding a new managed property takes minutes now.' },
  { name: 'Daniel Okafor', role: 'HMO operator · Leeds', quote: 'Per-room rent tracking via Open Banking replaced three spreadsheets. I can see exactly which room is late the day it happens.' },
];
const testimonialsLoop = [...testimonials, ...testimonials, ...testimonials, ...testimonials];

const BLOG_SLUGS = ['smoke-alarm-regulations', 'gas-safety-certificate-explained', 'open-banking-for-landlords'];

const H2 = (props: React.ComponentProps<typeof Box>) => (
  <Box
    component="h2"
    {...props}
    sx={{ fontFamily: F.display, fontWeight: V.fontWeight400, fontSize: V.sectionHeadingSize, lineHeight: V.lineHeight1_2, letterSpacing: V.letterSpacing_0_035em, margin: 0, ...props.sx }}
  />
);

const LandingPage: React.FC = () => {
  const stepCardsRef = useRef<HTMLOListElement>(null);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  useMarketingFonts();
  useSeoHead({
    title: "MillOS — Grinding through compliance, so you don't have to",
    description: 'MillOS is the AI compliance operating system, starting with UK property. Two agents book your engineers, arrange tenant access and reconcile your books. Save hours — just check in.',
    ogDescription: 'The instrument-grade cockpit for UK property compliance and accounting. AI tracks expiries, drafts emails and scores your portfolio.',
    canonicalPath: '/',
    jsonLd: [
      { '@context': 'https://schema.org', '@type': 'Organization', name: 'MillOS', url: SITE_URL, logo: `${SITE_URL}/millos-mark.svg` },
      { '@context': 'https://schema.org', '@type': 'WebSite', name: 'MillOS', url: SITE_URL },
    ],
  });

  const blogs = BLOG_SLUGS.map((slug) => {
    const a = getArticle(slug);
    if (!a) return null;
    return { slug, category: a.category, title: a.title, desc: a.description, img: cover(a) };
  }).filter(Boolean) as { slug: string; category: string; title: string; desc: string; img: string }[];

  // overflowX is 'clip', not 'hidden': any non-visible overflow on an ancestor — even just the
  // x-axis — detaches position:sticky from the viewport, which breaks MillosAgentsScroll's
  // scroll-pinned windmill below. 'clip' prevents the same horizontal bleed from decorative
  // elements without creating a new scroll container, so sticky still pins to the viewport.
  return (
    <ThemeProvider theme={homepageTheme}>
    <Box className="lv-marketing" data-homepage-theme="product" sx={{ ...homepageStyles, '--website-eyebrow-scale': 0.85, '& h2': { zoom: 1.05 }, background: C.white, color: C.ink, fontFamily: F.body, fontSize: V.fontSize16, lineHeight: V.lineHeight1_5, overflowX: 'clip', position: 'relative' }}>
      <MarketingNav />

      <MillosHero />

      {/* TRUST / ABOUT */}
      <Box component="section" id="about" aria-labelledby="about-heading" sx={{ padding: { xs: '48px 24px', md: '20px 40px' }, scrollMarginTop: '80px', minHeight: { md: 'calc(100svh - 80px)' }, boxSizing: 'border-box', display: 'flex', alignItems: 'center',
        '@media (min-width:640px) and (max-width:1100px)': { padding: '20px 28px 32px', minHeight: 'calc(100svh - 80px)', alignItems: 'flex-start' },
      }}>
        <Box sx={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
          '@media (min-width:640px) and (max-width:1100px)': { width: '100%', minHeight: 'calc(100svh - 132px)' },
        }}>
          <Box sx={{ position: 'relative', top: '24px', mb: '22px',
            '@media (min-width:640px) and (max-width:1100px)': { top: 0, mt: '16px', mb: '10px' },
          }}>
            <Eyebrow>What is MillOS</Eyebrow>
          </Box>
          <H2 id="about-heading" sx={{ position: 'relative', top: '24px', maxWidth: '64rem', fontSize: `clamp(${V.fontSize34}, 5.5svh, ${V.sectionHeadingSize})`, lineHeight: V.lineHeight1_1,
            '@media (min-width:640px) and (max-width:1100px)': { top: 0, fontSize: 'clamp(1.8rem, 4.5svh, 2.4rem)' },
          }}>
            The Agentic <Box component="span" sx={{ color: V.brandTeal }}>O</Box>perating <Box component="span" sx={{ color: V.brandTeal }}>S</Box>ystem for your<br />property portfolio
          </H2>
          <MillosProductPreview />
          <Box sx={{ position: 'relative', top: { xs: 0, md: '-34px' }, width: '100%', mt: { xs: '24px', md: '44px' }, pt: '16px',
            '@media (min-width:640px) and (max-width:1100px)': { top: 0, mt: 0, pt: '12px' },
          }}>
          <StatsDotFlow />
          <Box component="dl" aria-label="Why staying on top matters" sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', sm: 'repeat(4,minmax(0,1fr))' }, gap: { xs: '24px 16px', md: '24px' }, alignItems: 'start', m: 0 }}>
            {stats.map((st) => (
              <Box key={st.l} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', textAlign: 'center', pb: { xs: '8px', md: '32px' },
                '@media (min-width:640px) and (max-width:1100px)': { pb: '8px' },
              }}>
                <Box component="dt" sx={{ fontFamily: F.display, fontSize: { xs: 26, md: V.fontSize34 }, lineHeight: V.lineHeight1, letterSpacing: V.letterSpacing_0_03em, whiteSpace: 'nowrap',
                  '@media (min-width:640px) and (max-width:1100px)': { fontSize: 28 },
                }}>{st.v}</Box>
                <Box component="dd" sx={{ m: 0, fontSize: V.fontSize12, lineHeight: V.lineHeight1_45, color: C.body, maxWidth: '14rem' }}>
                  <Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, '@media (min-width:640px) and (max-width:1100px)': { display: 'none' } }}>{st.l}</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', md: 'none' }, '@media (min-width:640px) and (max-width:1100px)': { display: 'inline' } }}>{st.mobileLabel}</Box>
                </Box>
              </Box>
            ))}
          </Box>
          </Box>
        </Box>
      </Box>

      {/* HOW IT WORKS */}
      <Box component="section" id="works" sx={{ marginTop: '48px', padding: '12px', scrollMarginTop: '80px' }}>
        <Box sx={{ ...marketingDarkStyles, maxWidth: 1392, margin: '0 auto', background: C.heroGradient, borderRadius: V.borderRadius32px, padding: { xs: '56px 24px', md: '80px 40px' },
          [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: {
            padding: '16px 14px', minHeight: 'calc(100svh - 104px)',
            boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
          },
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', color: V.onDark,
            [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: { pt: '16px', pb: '16px' },
            '@media (max-width:1099.95px) and (max-height:700px)': { pt: '4px', pb: '4px' },
          }}>
            <Eyebrow variant="onImage">What MillOS does</Eyebrow>
            <H2 sx={{ maxWidth: '55rem', mt: '22px', color: V.onDark,
              [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: { fontSize: 'clamp(1.75rem, 4vw, 2.1rem)', mt: '24px', mb: '12px' },
              '@media (max-width:1099.95px) and (max-height:700px)': { mt: '12px', mb: '8px', fontSize: '1.6rem' },
            }}>AI agents that organise your data and act on your behalf.</H2>
          </Box>
          <Box sx={{ position: 'relative', marginTop: '56px', display: 'flex', flexDirection: 'column',
            [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: { mt: '16px', flex: 1 },
            '@media (max-width:1099.95px) and (max-height:700px)': { mt: '8px' },
          }}>
            <Box component="ol" role="list" ref={stepCardsRef} sx={{
              display: 'grid', gridTemplateColumns: `repeat(${steps.length},minmax(0,1fr))`,
              gap: '20px', listStyle: 'none', m: 0, p: 0,
              [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: {
                gridTemplateColumns: '1fr', ml: '46px', gap: '8px', flex: 1, alignContent: 'space-evenly',
              },
            }}>
              {steps.map((b) => (
                <Box component="li" data-marketing-surface="light" key={b.n} sx={{ display: 'flex', flexDirection: 'column', gap: '24px', background: b.n === '03' ? `color-mix(in srgb, ${V.brandTeal} 8%, ${C.white})` : C.white, border: b.n === '03' ? `1px solid ${withOpacity(V.brandTeal, 0.4)}` : '1px solid transparent', borderRadius: V.borderRadius32px, padding: '32px', height: '100%',
                  [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: { padding: '10px 12px', borderRadius: '16px', height: 'auto', boxSizing: 'border-box' },
                }}>
                  <Box sx={{
                    [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: { position: 'relative', pr: '32px' },
                  }}>
                    <Box sx={{ fontFamily: F.ui, fontSize: V.fontSize24, lineHeight: V.lineHeight1_1,
                      [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: { fontSize: 18 },
                    }}>
                      <Box component="span" sx={{ color: b.title.startsWith('You ') ? V.brandViolet : V.brandTeal, fontWeight: V.fontWeight700 }}>
                        {b.n === '03' ? 'AI agents' : b.title.split(' ')[0]}
                      </Box>
                      {b.n === '03' ? ' do the work' : b.title.slice(b.title.indexOf(' '))}
                    </Box>
                    <Box component="ul" sx={{ mt: '12px', mb: 0, pl: '20px', color: C.body, fontSize: V.fontSize15, '& > li + li': { mt: '8px' },
                      [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: { mt: '4px', pl: '16px', fontSize: 13, lineHeight: 1.35 },
                    }}>
                      {b.bullets.map((bullet, index) => (
                        <Box component="li" key={bullet} id={index > 0 ? `step-${b.n}-bullet-${index}` : undefined}
                          sx={index > 0 && expandedStep !== b.n ? {
                            [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: { display: 'none' },
                          } : undefined}>
                          {bullet}
                        </Box>
                      ))}
                    </Box>
                    <Box component="button" type="button" aria-expanded={expandedStep === b.n}
                      aria-label={`${expandedStep === b.n ? 'Collapse' : 'Expand'} ${b.title}`}
                      aria-controls={b.bullets.slice(1).map((_, index) => `step-${b.n}-bullet-${index + 1}`).join(' ')}
                      onClick={() => setExpandedStep(previous => previous === b.n ? null : b.n)}
                      sx={{
                        display: 'none', mt: 2, p: '8px 0', border: 0, background: 'none',
                        color: V.tealText, font: 'inherit', fontWeight: V.fontWeight600, cursor: 'pointer',
                        [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: {
                          display: 'inline-flex', position: 'absolute', right: '-8px', top: '50%',
                          transform: 'translateY(-50%)', mt: 0, width: 36, minHeight: 44,
                          p: 0, alignItems: 'center', justifyContent: 'center',
                        },
                      }}>
                      {expandedStep === b.n ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            <StepTimeline count={steps.length} cardsRef={stepCardsRef} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '44px', flexWrap: 'wrap',
            [`@media (max-width:${STEP_TIMELINE_CONFIG.BREAKPOINT_VERTICAL_PX - 0.05}px)`]: {
              mt: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px',
              '& > a': { padding: '10px 8px', fontSize: 13 },
            },
            '@media (max-width:1099.95px) and (max-height:700px)': { mt: '8px' },
          }}>
            <Box component={RouterLink} to="/register" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: C.millosMint, color: V.buttonText, fontWeight: V.fontWeight600, borderRadius: V.borderRadius32px, padding: '14px 26px', fontSize: V.fontSize16, '&:hover': { background: V.brandBlue, color: V.buttonText } }}>
              Try now for free <Box component="span">→</Box>
            </Box>
            <Box component={RouterLink} to="/book-a-call" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: V.paperAlpha14, border: `1px solid ${V.paperAlpha40}`, color: V.onDark, borderRadius: V.borderRadius32px, padding: '14px 26px', fontSize: V.fontSize16, '&:hover': { background: C.millosMint, borderColor: C.millosMint, color: V.buttonText } }}>
              Book a call
            </Box>
          </Box>
        </Box>
      </Box>

      {/* TWO PILLARS — scroll-pinned windmill */}
      <Box component="section" id="features">
        <MillosAgentsScroll />
      </Box>

      {/* PLATFORM ORBIT */}
      <MillosOrbit />

      {/* TESTIMONIALS */}
      <Box component="section" sx={{ padding: '96px 0' }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Eyebrow>Testimonials</Eyebrow>
          <H2 sx={{ maxWidth: '45rem', marginTop: '24px' }}>
            What UK landlords <Box component="em" sx={{ fontStyle: 'italic' }}>are saying</Box>
          </H2>
          <Box sx={{ maxWidth: '38rem', marginTop: '24px', fontSize: V.fontSize16, color: C.body }}>Landlords and agents love how MillOS makes a missed deadline impossible.</Box>
        </Box>
        <Box sx={{ position: 'relative', marginTop: '64px', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', gap: '20px', width: 'max-content', animation: 'lv-marquee 58s linear infinite' }}>
            {testimonialsLoop.map((t, i) => (
              <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: '24px', background: C.mist, border: `1px solid ${C.glassBorder}`, borderRadius: V.borderRadius32px, minWidth: '28rem', maxWidth: '28rem', padding: '88px 30px 28px', position: 'relative', overflow: 'hidden' }}>
                <InitialsAvatar name={t.name} size={48} style={{ position: 'absolute', top: 24, left: 24 }} />
                <Box sx={{ position: 'absolute', top: 24, right: 24 }}>
                  <QuoteIcon size={40} />
                </Box>
                <Box sx={{ color: C.body, fontSize: V.fontSize15 }}>{t.quote}</Box>
                <Box>
                  <Box sx={{ fontWeight: V.fontWeight500 }}>{t.name}</Box>
                  <Box sx={{ color: C.body, fontSize: V.fontSize14 }}>{t.role}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* BLOG */}
      <Box component="section" aria-label="Blog and articles" sx={{ padding: '0 52px 96px',
        '@media (max-width:639px)': { padding: '0 20px 48px' },
      }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Eyebrow>Blog and articles</Eyebrow>
          <H2 sx={{ maxWidth: '45rem', marginTop: '24px',
            '@media (max-width:639px)': { fontSize: 28, mt: '16px' },
          }}>
            The latest for <Box component="em" sx={{ fontStyle: 'italic' }}>UK landlords</Box>
          </H2>
          <Box sx={{ maxWidth: '38rem', marginTop: '24px', fontSize: V.fontSize16, color: C.body,
            '@media (max-width:639px)': { fontSize: 14, mt: '12px' },
          }}>Compliance rules, tax changes and accounting playbooks — written by our UK property specialists.</Box>
        </Box>
        <Box role="group" aria-label="Latest articles" sx={{ maxWidth: 1000, margin: '64px auto 0', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: '20px',
          '@media (max-width:639px)': {
            display: 'flex', gap: '12px', mt: '24px', overflowX: 'auto', scrollSnapType: 'x mandatory',
            pb: '12px', '& > a': { flex: '0 0 72%', minWidth: 0, gap: '10px', scrollSnapAlign: 'start' },
          },
        }}>
          {blogs.map((bl) => (
            <Box key={bl.slug} component={RouterLink} to={`/blog/${bl.slug}`} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Box sx={{ borderRadius: V.borderRadius24px, overflow: 'hidden', aspectRatio: '16/11', position: 'relative',
                '@media (max-width:639px)': { aspectRatio: '16/9', borderRadius: '16px' },
              }}>
                <Box component="img" loading="lazy" src={bl.img} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <Box component="span" sx={{ position: 'absolute', top: 14, left: 14, background: C.white, color: C.millosBlue, borderRadius: V.borderRadius32px, padding: '5px 12px', fontSize: V.fontSize11, fontWeight: V.fontWeight500, letterSpacing: V.letterSpacing0_06rem, textTransform: 'uppercase',
                  '@media (max-width:639px)': { top: 8, left: 8, px: '8px', fontSize: 9 },
                }}>{bl.category}</Box>
              </Box>
              <Box>
                <Box component="h3" sx={{ fontSize: V.fontSize20, fontWeight: V.fontWeight500, lineHeight: V.lineHeight1_35, margin: 0,
                  '@media (max-width:639px)': { fontSize: 16 },
                }}>{bl.title}</Box>
                <Box component="p" sx={{ marginTop: '8px', color: C.body, fontSize: V.fontSize15, margin: '8px 0 0',
                  '@media (max-width:639px)': { display: 'none' },
                }}>{bl.desc}</Box>
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '48px',
          '@media (max-width:639px)': { mt: '20px' },
        }}>
          <Box component={RouterLink} to="/blog" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.white, color: C.navy, border: `1px solid ${C.glassBorder}`, fontWeight: V.fontWeight500, borderRadius: V.borderRadius32px, padding: '12px 20px', fontSize: V.fontSize16, '&:hover': { background: C.millosMint, borderColor: C.millosMint, color: V.buttonText } }}>
            Read the blog
          </Box>
        </Box>
      </Box>

      {/* AVAILABILITY / CLOSING CTA */}
      <MillosAvailabilityCta />

      <MarketingFooter />
    </Box>
    </ThemeProvider>
  );
};

export default LandingPage;
